import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { stripPatronymicFromOfficialFopTitle } from "../utils/stripFopPatronymic";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const uah = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return "—";
    return new Intl.NumberFormat("uk-UA", {
        style: "currency",
        currency: "UAH",
        maximumFractionDigits: 2,
    }).format(n);
};

/** Напр. «17 квітня 18:09» (ISO або рядок, який їсть Date) */
const formatTxDate = (value) => {
    if (value == null || value === "") return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const datePart = new Intl.DateTimeFormat("uk-UA", {
        day: "numeric",
        month: "long",
    }).format(d);
    const timePart = new Intl.DateTimeFormat("uk-UA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(d);
    return `${datePart} ${timePart}`.replace(/\s+/g, " ").trim();
};

/** Колонки з довгим текстом: ellipsis без стискання дати/суми (maxWidth:0 ламає перші колонки при fixed layout). */
const ellipsisTextColSx = {
    maxWidth: 360,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "top",
};

const dateColSx = {
    width: "1%",
    minWidth: 168,
    whiteSpace: "nowrap",
    verticalAlign: "top",
    pr: 2,
};

const amountColSx = {
    width: "1%",
    minWidth: 104,
    whiteSpace: "nowrap",
    verticalAlign: "top",
    pl: 1,
};

const NovaPayPage = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

    const today = dayjs();
    const weekAgo = today.subtract(7, "day");

    const [dateFrom, setDateFrom] = useState(weekAgo.format("YYYY-MM-DD"));
    const [dateTo, setDateTo] = useState(today.format("YYYY-MM-DD"));
    const [accounts, setAccounts] = useState([]);
    const [balances, setBalances] = useState([]);
    const [fopLabelByKey, setFopLabelByKey] = useState({});
    const [loadingTx, setLoadingTx] = useState(false);
    const [loadingBalances, setLoadingBalances] = useState(true);
    const [loadingFopLabels, setLoadingFopLabels] = useState(true);
    const [balancesError, setBalancesError] = useState("");
    const [fopLabelsError, setFopLabelsError] = useState("");
    const [txError, setTxError] = useState("");
    /** Діалог введення нової пари токенів NovaPay: { key, title } | null */
    const [tokenDialog, setTokenDialog] = useState(null);
    const [refreshToken, setRefreshToken] = useState("");
    const [publicCertificate, setPublicCertificate] = useState("");
    const [savingToken, setSavingToken] = useState(false);
    const [tokenError, setTokenError] = useState("");

    const resolveFopTitle = useCallback(
        (key, fallbackName) => {
            if (key && fopLabelByKey[key]) return fopLabelByKey[key];
            const stripped = stripPatronymicFromOfficialFopTitle(fallbackName);
            return stripped || fallbackName || "—";
        },
        [fopLabelByKey]
    );

    const loadFopLabels = useCallback(async () => {
        setLoadingFopLabels(true);
        setFopLabelsError("");
        try {
            /** axios проходить через interceptor — оновлює access JWT при 401 (fetch цього не робить). */
            const { data: payload } = await axios.get("/api/users/novapay-fop-options");
            if (!Array.isArray(payload)) {
                setFopLabelByKey({});
                setFopLabelsError("Некоректна відповідь (підписи ФОП)");
                return;
            }
            const map = {};
            payload.forEach((row) => {
                if (row?.key) map[row.key] = row.displayLabel || row.label || "";
            });
            setFopLabelByKey(map);
        } catch (e) {
            const msg = e?.response?.data?.error;
            setFopLabelsError(typeof msg === "string" ? msg : "Не вдалося завантажити підписи ФОП");
            setFopLabelByKey({});
        } finally {
            setLoadingFopLabels(false);
        }
    }, []);

    const loadBalances = useCallback(async () => {
        setLoadingBalances(true);
        setBalancesError("");
        try {
            const { data: payload } = await axios.get("/api/novapay/balances");
            if (!Array.isArray(payload)) {
                setBalances([]);
                setBalancesError("Некоректна відповідь сервера");
                return;
            }
            setBalances(payload);
        } catch (e) {
            const msg = e?.response?.data?.error;
            setBalancesError(typeof msg === "string" ? msg : "Не вдалося завантажити залишки");
            setBalances([]);
        } finally {
            setLoadingBalances(false);
        }
    }, []);

    const loadTransactions = async () => {
        setTxError("");
        setLoadingTx(true);
        try {
            const { data: result } = await axios.post("/api/novapay/transactions/all", { dateFrom, dateTo });
            if (!Array.isArray(result)) {
                setTxError("Некоректна відповідь сервера");
                setAccounts([]);
                return;
            }
            setAccounts(result);
        } catch (e) {
            const msg = e?.response?.data?.error;
            setTxError(typeof msg === "string" ? msg : "Не вдалося отримати транзакції");
            setAccounts([]);
        } finally {
            setLoadingTx(false);
        }
    };

    useEffect(() => {
        loadBalances();
        loadFopLabels();
    }, [loadBalances, loadFopLabels]);

    const openTokenDialog = (b) => {
        setRefreshToken("");
        setPublicCertificate("");
        setTokenError("");
        setTokenDialog({ key: b.key, title: resolveFopTitle(b.key, b.name) });
    };

    const saveToken = async () => {
        setTokenError("");
        setSavingToken(true);
        try {
            await axios.post("/api/novapay/credentials", {
                fopKey: tokenDialog.key,
                refreshToken,
                publicCertificate,
            });
            setTokenDialog(null);
            loadBalances();
        } catch (e) {
            const msg = e?.response?.data?.error;
            setTokenError(typeof msg === "string" ? msg : "Не вдалося зберегти токен");
        } finally {
            setSavingToken(false);
        }
    };

    const balancesSorted = useMemo(() => {
        return [...balances].sort((a, b) => {
            const la = resolveFopTitle(a.key, a.name);
            const lb = resolveFopTitle(b.key, b.name);
            return la.localeCompare(lb, "uk");
        });
    }, [balances, resolveFopTitle]);

    const renderMobileTransactions = (acc) => {
        const rows = acc.transactions || [];
        if (rows.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                    Немає операцій за обраний період
                </Typography>
            );
        }
        return (
            <List dense disablePadding>
                {rows.map((tx, idx) => (
                    <React.Fragment key={`${tx.date}-${idx}`}>
                        {idx > 0 && <Divider component="li" />}
                        <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
                            <ListItemText
                                primary={
                                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                                        <Typography variant="subtitle2" component="span" sx={{ flexShrink: 0 }}>
                                            {formatTxDate(tx.date)}
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            component="span"
                                            fontWeight={700}
                                            sx={{ flexShrink: 0 }}
                                            color={Number(tx.amount) < 0 ? "error.main" : "success.main"}
                                        >
                                            {uah(tx.amount)}
                                        </Typography>
                                    </Stack>
                                }
                                secondary={
                                    <Stack component="span" spacing={0.5} sx={{ mt: 0.5, minWidth: 0 }}>
                                        {tx.from && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                component="span"
                                                display="block"
                                                noWrap
                                                title={tx.from}
                                            >
                                                Від: {tx.from}
                                            </Typography>
                                        )}
                                        {tx.to && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                component="span"
                                                display="block"
                                                noWrap
                                                title={tx.to}
                                            >
                                                Кому: {tx.to}
                                            </Typography>
                                        )}
                                        {tx.comment && (
                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                                component="span"
                                                display="block"
                                                noWrap
                                                title={tx.comment}
                                            >
                                                {tx.comment}
                                            </Typography>
                                        )}
                                    </Stack>
                                }
                            />
                        </ListItem>
                    </React.Fragment>
                ))}
            </List>
        );
    };

    const renderDesktopTable = (acc) => {
        const rows = acc.transactions || [];
        return (
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    maxWidth: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "auto" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...dateColSx, bgcolor: "background.paper", zIndex: 1 }}>Дата</TableCell>
                            <TableCell
                                align="right"
                                sx={{ ...amountColSx, bgcolor: "background.paper", zIndex: 1 }}
                            >
                                Сума
                            </TableCell>
                            <TableCell sx={{ bgcolor: "background.paper", zIndex: 1, minWidth: 160 }}>Від кого</TableCell>
                            <TableCell sx={{ bgcolor: "background.paper", zIndex: 1, minWidth: 160 }}>Кому</TableCell>
                            <TableCell sx={{ bgcolor: "background.paper", zIndex: 1, minWidth: 200 }}>Призначення</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant="body2" color="text.secondary">
                                        Немає операцій за обраний період
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((tx, idx) => (
                                <TableRow key={`${tx.date}-${idx}`} hover>
                                    <TableCell sx={dateColSx}>{formatTxDate(tx.date)}</TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            ...amountColSx,
                                            fontWeight: 700,
                                            color:
                                                Number(tx.amount) < 0
                                                    ? "error.main"
                                                    : Number(tx.amount) > 0
                                                      ? "success.main"
                                                      : "text.primary",
                                        }}
                                    >
                                        {uah(tx.amount)}
                                    </TableCell>
                                    <TableCell sx={ellipsisTextColSx} title={tx.from || ""}>
                                        {tx.from}
                                    </TableCell>
                                    <TableCell sx={ellipsisTextColSx} title={tx.to || ""}>
                                        {tx.to}
                                    </TableCell>
                                    <TableCell sx={{ ...ellipsisTextColSx, maxWidth: 480 }} title={tx.comment || ""}>
                                        {tx.comment}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" component="h1" gutterBottom>
                        Транзакції NovaPay
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Оберіть період і натисніть «Завантажити». Залишки оновлюються автоматично при відкритті сторінки.
                    </Typography>
                </Box>

                <Card variant="outlined">
                    <CardContent>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            <TextField
                                label="Від"
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ minWidth: { xs: "100%", sm: 160 } }}
                            />
                            <TextField
                                label="До"
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                sx={{ minWidth: { xs: "100%", sm: 160 } }}
                            />
                            <Box sx={{ flex: { sm: "0 0 auto" } }}>
                                <Button
                                    variant="contained"
                                    onClick={loadTransactions}
                                    disabled={loadingTx || !dateFrom || !dateTo}
                                    fullWidth={isNarrow}
                                    sx={{ minWidth: { sm: 160 } }}
                                >
                                    {loadingTx ? <CircularProgress size={22} color="inherit" /> : "Завантажити"}
                                </Button>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {txError && (
                    <Alert severity="error" onClose={() => setTxError("")}>
                        {txError}
                    </Alert>
                )}

                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1.5}>
                        <Typography variant="h6" component="h2">
                            Залишки на рахунках
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => {
                                loadFopLabels();
                                loadBalances();
                            }}
                            disabled={loadingBalances || loadingFopLabels}
                        >
                            Оновити
                        </Button>
                    </Stack>
                    {fopLabelsError && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            {fopLabelsError}
                        </Alert>
                    )}
                    {loadingBalances ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                        </Box>
                    ) : balancesError ? (
                        <Alert severity="warning">{balancesError}</Alert>
                    ) : balances.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Немає даних про рахунки
                        </Typography>
                    ) : (
                        <Stack spacing={1} divider={<Divider flexItem />}>
                            {balancesSorted.map((b, i) => (
                                <Stack
                                    key={b.key || b.name || i}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="baseline"
                                    gap={2}
                                    sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}
                                >
                                    <Typography variant="body2" sx={{ minWidth: 0, flex: "1 1 auto" }}>
                                        {resolveFopTitle(b.key, b.name)}
                                    </Typography>
                                    {b.error ? (
                                        <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                                            <Typography variant="body2" color="error" fontWeight={700}>
                                                {b.needsToken ? "потрібен токен" : "помилка"}
                                            </Typography>
                                            {b.needsToken && (
                                                <Button size="small" variant="outlined" onClick={() => openTokenDialog(b)}>
                                                    Ввести токен
                                                </Button>
                                            )}
                                        </Stack>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            fontWeight={700}
                                            sx={{ flexShrink: 0, textAlign: { xs: "left", sm: "right" } }}
                                        >
                                            {uah(b.balance)}
                                        </Typography>
                                    )}
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </Box>

                {accounts.length > 0 && (
                    <Stack spacing={3}>
                        {accounts.map((acc, i) => (
                            <Card key={acc.key || acc.name || i} variant="outlined">
                                <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        {resolveFopTitle(acc.key, acc.name)}
                                    </Typography>
                                    {acc.error ? (
                                        <Alert severity="error">
                                            {acc.needsToken
                                                ? "Потрібен новий токен NovaPay — введіть його в блоці «Залишки на рахунках»"
                                                : "Помилка підключення до цього рахунку"}
                                        </Alert>
                                    ) : isNarrow ? (
                                        renderMobileTransactions(acc)
                                    ) : (
                                        renderDesktopTable(acc)
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}

                {!loadingTx && accounts.length === 0 && !txError && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                        Натисніть «Завантажити», щоб побачити транзакції за період
                    </Typography>
                )}
            </Stack>

            <Dialog open={Boolean(tokenDialog)} onClose={() => !savingToken && setTokenDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>Токен NovaPay — {tokenDialog?.title}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Згенеруйте пару в кабінеті NovaPay: Система → Налаштування → API. Далі застосунок
                            оновлює її сам.
                        </Typography>
                        <TextField
                            label="refresh_token"
                            value={refreshToken}
                            onChange={(e) => setRefreshToken(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                        <TextField
                            label="public_certificate"
                            value={publicCertificate}
                            onChange={(e) => setPublicCertificate(e.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                        />
                        {tokenError && <Alert severity="error">{tokenError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTokenDialog(null)} disabled={savingToken}>
                        Скасувати
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveToken}
                        disabled={savingToken || !refreshToken.trim() || !publicCertificate.trim()}
                    >
                        {savingToken ? "Перевірка…" : "Зберегти"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default NovaPayPage;
